import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import {
  createDeck,
  downloadCSV,
  getCards,
  getDecks,
  isV2Duplicate,
  isV2Ok,
  removeCardById,
  removeDeck,
  updateCard,
  type Card,
  type Deck,
} from '../adapters/cards/cardsApi.ts';
import { useAuth } from '../hooks/useAuth.ts';
import { useI18n } from '../i18n/index.ts';
import { LoginDialog } from '../components/LoginDialog.tsx';

export type CardsPageProps = {
  settingsStore: SettingsStore;
  onBack: () => void;
};

const PAGE_SIZE = 25;

type EditDraft = {
  id: string;
  deck: string;
  deckName?: string;
  front: string;
  frontTrans: string;
  refSent: string;
  refTrans: string;
  frontDesc: string;
  backDesc: string;
  nbsCache: Record<string, string>;
};

function deckLabel(deck: Deck, uncategorizedLabel: string): string {
  const name = deck.id === 'uncategorized' ? uncategorizedLabel : deck.name;
  return deck.numOfCards !== undefined ? `${name} (${deck.numOfCards})` : name;
}

export function CardsPage({ settingsStore, onBack }: CardsPageProps) {
  const { t } = useI18n();
  const auth = useAuth(settingsStore, { revalidateOnMount: true });
  const [loginOpen, setLoginOpen] = useState(false);
  const [settings, setSettings] = useState(() => settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedDeck, setSelectedDeck] = useState('uncategorized');
  const [sort, setSort] = useState('createdAt_desc');
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [newDeckName, setNewDeckName] = useState('');
  const [editing, setEditing] = useState<EditDraft | null>(null);
  const [editBaseline, setEditBaseline] = useState<EditDraft | null>(null);
  const [nbsTab, setNbsTab] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => settingsStore.subscribe(() => {
    setSettings(settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS);
  }), [settingsStore]);

  const { userId, userToken } = settings;
  const signedIn = Boolean(userId && userToken);

  const deckOptions = useMemo(() => {
    const uncategorized: Deck = {
      id: 'uncategorized',
      name: t('cardsUncategorized'),
      numOfCards: decks.find((d) => d.id === 'uncategorized')?.numOfCards,
    };
    const rest = decks.filter((d) => d.id !== 'uncategorized');
    return [uncategorized, ...rest];
  }, [decks, t]);

  const editDirty = useMemo(() => {
    if (!editing || !editBaseline) return false;
    return (
      editing.deck !== editBaseline.deck
      || editing.front !== editBaseline.front
      || editing.frontTrans !== editBaseline.frontTrans
      || editing.refSent !== editBaseline.refSent
      || editing.refTrans !== editBaseline.refTrans
      || editing.frontDesc !== editBaseline.frontDesc
      || editing.backDesc !== editBaseline.backDesc
    );
  }, [editing, editBaseline]);

  const loadDecks = useCallback(async () => {
    if (!signedIn) return;
    try {
      const items = await getDecks(userId, userToken);
      setDecks(items);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    }
  }, [signedIn, userId, userToken, t]);

  const reloadCards = useCallback(async () => {
    if (!signedIn) return;
    setLoading(true);
    setMessage(null);
    try {
      const items = await getCards(userId, userToken, selectedDeck, undefined, sort);
      setCards(items);
      setCursor(items.length > 0 ? items[items.length - 1]?.id : undefined);
      setHasMore(!(items.length === 0 || items.length < PAGE_SIZE));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    } finally {
      setLoading(false);
    }
  }, [signedIn, userId, userToken, selectedDeck, sort, t]);

  const loadMoreCards = useCallback(async () => {
    if (!signedIn || loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);
    try {
      const items = await getCards(userId, userToken, selectedDeck, cursor, sort);
      setCards((prev) => [...prev, ...items]);
      if (items.length > 0) setCursor(items[items.length - 1]?.id);
      if (items.length === 0 || items.length < PAGE_SIZE) setHasMore(false);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    } finally {
      setLoadingMore(false);
    }
  }, [signedIn, loadingMore, hasMore, cursor, userId, userToken, selectedDeck, sort, t]);

  useEffect(() => {
    if (!signedIn) return;
    void loadDecks();
  }, [signedIn, loadDecks]);

  useEffect(() => {
    if (!signedIn) return;
    void reloadCards();
  }, [signedIn, reloadCards]);

  const onListScroll = useCallback(() => {
    const list = listRef.current;
    if (!list || loadingMore || !hasMore) return;
    const padding = 50;
    if (list.scrollTop + list.clientHeight + padding >= list.scrollHeight) {
      void loadMoreCards();
    }
  }, [loadingMore, hasMore, loadMoreCards]);

  const bumpDeckCount = useCallback((deckId: string, delta: number) => {
    if (deckId === 'uncategorized') return;
    setDecks((prev) =>
      prev.map((deck) =>
        deck.id === deckId && deck.numOfCards !== undefined
          ? { ...deck, numOfCards: Math.max(0, deck.numOfCards + delta) }
          : deck,
      ),
    );
  }, []);

  const handleCreateDeck = async () => {
    if (!signedIn) return;
    const name = newDeckName.trim();
    if (!name) return;
    setMessage(null);
    try {
      const result = await createDeck(userId, userToken, name);
      if (!result || !isV2Ok(result)) {
        setMessage(isV2Duplicate(result) ? t('cardsDuplicate') : t('cardsFailed'));
        return;
      }
      const newId = result.id ?? (result.data as { id?: string } | undefined)?.id;
      setNewDeckName('');
      if (newId) {
        setDecks((prev) => [...prev, { id: newId, name, numOfCards: 0 }]);
        setSelectedDeck(newId);
      } else {
        await loadDecks();
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    }
  };

  const handleDeleteDeck = async () => {
    if (!signedIn || selectedDeck === 'uncategorized') return;
    if (!window.confirm(t('cardsRemoveDeckConfirm'))) return;
    setMessage(null);
    try {
      const json = await removeDeck(userId, userToken, selectedDeck);
      if (!json || !isV2Ok(json)) {
        setMessage(t('cardsFailed'));
        return;
      }
      setSelectedDeck('uncategorized');
      await loadDecks();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    }
  };

  const handleDeleteCard = async (id: string) => {
    if (!signedIn) return;
    setMessage(null);
    const found = cards.find((card) => card.id === id);
    const prevCards = cards;
    const prevDecks = decks;
    setCards((prev) => prev.filter((card) => card.id !== id));
    if (found) bumpDeckCount(found.deck, -1);
    try {
      const json = await removeCardById(userId, userToken, id);
      if (!json || !isV2Ok(json)) {
        setCards(prevCards);
        setDecks(prevDecks);
        setMessage(t('cardsFailed'));
      }
    } catch (err) {
      setCards(prevCards);
      setDecks(prevDecks);
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    }
  };

  const openEdit = (card: Card) => {
    const cache =
      card.nbsCache && typeof card.nbsCache === 'object'
        ? Object.fromEntries(
            Object.entries(card.nbsCache).map(([k, v]) => [k, String(v ?? '')]),
          )
        : {};
    const draft: EditDraft = {
      id: card.id,
      deck: card.deck || selectedDeck,
      deckName: card.deckName,
      front: String(card.front ?? ''),
      frontTrans: String(card.frontTrans ?? ''),
      refSent: String(card.refSent ?? ''),
      refTrans: String(card.refTrans ?? ''),
      frontDesc: String(card.frontDesc ?? ''),
      backDesc: String(card.backDesc ?? ''),
      nbsCache: cache,
    };
    setEditing(draft);
    setEditBaseline(draft);
    setNbsTab(Object.keys(cache)[0] ?? null);
  };

  const handleSaveEdit = async () => {
    if (!signedIn || !editing || !editDirty) return;
    setMessage(null);
    const deckName =
      editing.deck === 'uncategorized'
        ? t('cardsUncategorized')
        : decks.find((d) => d.id === editing.deck)?.name ?? editing.deckName;
    const prevDeck = editBaseline?.deck;
    try {
      const json = await updateCard(userId, userToken, {
        id: editing.id,
        deck: editing.deck,
        deckName,
        front: editing.front,
        frontTrans: editing.frontTrans,
        refSent: editing.refSent,
        refTrans: editing.refTrans,
        frontDesc: editing.frontDesc,
        backDesc: editing.backDesc,
      });
      if (!json || !isV2Ok(json)) {
        setMessage(t('cardsFailed'));
        return;
      }
      if (prevDeck && prevDeck !== editing.deck) {
        bumpDeckCount(prevDeck, -1);
        bumpDeckCount(editing.deck, 1);
      }
      setEditing(null);
      setEditBaseline(null);
      if (editing.deck !== selectedDeck) setSelectedDeck(editing.deck);
      else await reloadCards();
      await loadDecks();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    }
  };

  const handleExport = async () => {
    if (!signedIn) return;
    setExporting(true);
    setMessage(null);
    try {
      const deck =
        decks.find((d) => d.id === selectedDeck)
        ?? {
          id: selectedDeck,
          name: selectedDeck === 'uncategorized' ? t('cardsUncategorized') : selectedDeck,
        };
      const json = await downloadCSV(userId, userToken, deck);
      const url = isV2Ok(json) ? (json.data as { url?: string }).url : undefined;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')?.focus();
      } else {
        setMessage(t('cardsFailed'));
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : t('cardsFailed'));
    } finally {
      setExporting(false);
    }
  };

  if (!signedIn) {
    return (
      <div className="cards-page" data-testid="cards-page">
        <div className="cards-page__shell">
          <header className="cards-page__header">
            <button type="button" className="cards-page__icon-btn" onClick={onBack} aria-label={t('back')}>
              ←
            </button>
            <div className="cards-page__heading">
              <p className="cards-page__eyebrow">{t('appName')}</p>
              <h1>{t('cardsTitle')}</h1>
            </div>
          </header>
          <div className="cards-page__gate">
            <p className="cards-page__hint">{t('cardsLoginRequired')}</p>
            <button
              type="button"
              className="cards-page__btn cards-page__btn--primary"
              data-testid="cards-sign-in"
              onClick={() => setLoginOpen(true)}
            >
              {t('cardsSignInCta')}
            </button>
          </div>
        </div>
        <LoginDialog auth={auth} open={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  const nbsKeys = editing ? Object.keys(editing.nbsCache) : [];

  return (
    <div className="cards-page" data-testid="cards-page">
      <div className="cards-page__shell">
        <header className="cards-page__header">
          <button type="button" className="cards-page__icon-btn" onClick={onBack} aria-label={t('back')}>
            ←
          </button>
          <div className="cards-page__heading">
            <p className="cards-page__eyebrow">{t('appName')}</p>
            <h1>{t('cardsTitle')}</h1>
          </div>
          <button
            type="button"
            className="cards-page__icon-btn"
            data-testid="cards-refresh"
            aria-label={t('cardsRefresh')}
            title={t('cardsRefresh')}
            onClick={() => {
              void loadDecks();
              void reloadCards();
            }}
          >
            ↻
          </button>
        </header>

        <section className="cards-page__controls" aria-label={t('cardsTitle')}>
          <div className="cards-page__filters">
            <label className="cards-page__field">
              <span>{t('cardsDeck')}</span>
              <select
                data-testid="cards-deck-select"
                value={selectedDeck}
                onChange={(e) => setSelectedDeck(e.target.value)}
              >
                {deckOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {deckLabel(d, t('cardsUncategorized'))}
                  </option>
                ))}
              </select>
            </label>
            <label className="cards-page__field">
              <span>{t('cardsSort')}</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="createdAt_desc">{t('cardsSortCreatedDesc')}</option>
                <option value="createdAt_asc">{t('cardsSortCreatedAsc')}</option>
                <option value="updatedAt_desc">{t('cardsSortUpdatedDesc')}</option>
                <option value="updatedAt_asc">{t('cardsSortUpdatedAsc')}</option>
              </select>
            </label>
          </div>

          <div className="cards-page__actions-row">
            <button
              type="button"
              className="cards-page__btn cards-page__btn--ghost"
              data-testid="cards-export"
              disabled={exporting}
              onClick={() => void handleExport()}
            >
              {exporting ? t('cardsLoading') : t('cardsExportCsv')}
            </button>
            <button
              type="button"
              className="cards-page__btn cards-page__btn--danger-ghost"
              data-testid="cards-remove-deck"
              disabled={selectedDeck === 'uncategorized'}
              onClick={() => void handleDeleteDeck()}
            >
              {t('cardsRemoveDeck')}
            </button>
          </div>

          <div className="cards-page__create">
            <input
              type="text"
              className="cards-page__input"
              placeholder={t('cardsDeckName')}
              value={newDeckName}
              onChange={(e) => setNewDeckName(e.target.value)}
              data-testid="cards-create-deck-name"
            />
            <button
              type="button"
              className="cards-page__btn cards-page__btn--primary"
              data-testid="cards-create-deck"
              disabled={!newDeckName.trim()}
              onClick={() => void handleCreateDeck()}
            >
              {t('cardsCreateDeck')}
            </button>
          </div>
        </section>

        {message ? <p className="cards-page__error" role="alert">{message}</p> : null}

        <div className="cards-page__list-wrap">
          {loading ? (
            <p className="cards-page__status">{t('cardsLoading')}</p>
          ) : null}
          <ul
            className="cards-page__list"
            data-testid="cards-list"
            ref={listRef}
            onScroll={onListScroll}
          >
            {cards.map((card) => (
              <li key={card.id} className="cards-page__card">
                <div className="cards-page__card-body">
                  <div className="cards-page__card-front">{card.front}</div>
                  {card.frontTrans ? (
                    <div className="cards-page__card-trans">{card.frontTrans}</div>
                  ) : null}
                  {card.refSent ? (
                    <div className="cards-page__card-ref">{card.refSent}</div>
                  ) : null}
                </div>
                <div className="cards-page__card-actions">
                  <button
                    type="button"
                    className="cards-page__text-btn"
                    onClick={() => openEdit(card)}
                  >
                    {t('cardsEditCard')}
                  </button>
                  <button
                    type="button"
                    className="cards-page__text-btn cards-page__text-btn--danger"
                    onClick={() => void handleDeleteCard(card.id)}
                  >
                    {t('cardsDeleteCard')}
                  </button>
                </div>
              </li>
            ))}
            {loadingMore ? (
              <li className="cards-page__status" data-testid="cards-loading-more">
                {t('cardsLoading')}
              </li>
            ) : null}
          </ul>
          {!loading && cards.length === 0 ? (
            <div className="cards-page__empty">
              <p className="cards-page__empty-title">{t('cardsEmpty')}</p>
            </div>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="cards-page__modal" role="dialog" aria-modal="true" data-testid="cards-edit-modal">
          <div className="cards-page__modal-body">
            <header className="cards-page__modal-header">
              <h2>{t('cardsEditCard')}</h2>
              <button
                type="button"
                className="cards-page__icon-btn"
                aria-label={t('cardsClose')}
                onClick={() => { setEditing(null); setEditBaseline(null); }}
              >
                ×
              </button>
            </header>
            <label className="cards-page__field">
              <span>{t('cardsDeck')}</span>
              <select
                value={editing.deck}
                onChange={(e) => setEditing({ ...editing, deck: e.target.value })}
              >
                {deckOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {deckLabel(d, t('cardsUncategorized'))}
                  </option>
                ))}
              </select>
            </label>
            <label className="cards-page__field">
              <span>{t('cardsFieldFront')}</span>
              <input
                value={editing.front}
                onChange={(e) => setEditing({ ...editing, front: e.target.value })}
              />
            </label>
            <label className="cards-page__field">
              <span>{t('cardsFieldFrontTrans')}</span>
              <input
                value={editing.frontTrans}
                onChange={(e) => setEditing({ ...editing, frontTrans: e.target.value })}
              />
            </label>
            <label className="cards-page__field">
              <span>{t('cardsFieldRefSent')}</span>
              <textarea
                rows={3}
                value={editing.refSent}
                onChange={(e) => setEditing({ ...editing, refSent: e.target.value })}
              />
            </label>
            <label className="cards-page__field">
              <span>{t('cardsFieldRefTrans')}</span>
              <textarea
                rows={3}
                value={editing.refTrans}
                onChange={(e) => setEditing({ ...editing, refTrans: e.target.value })}
              />
            </label>
            <label className="cards-page__field">
              <span>{t('cardsFieldFrontDesc')}</span>
              <textarea
                rows={2}
                value={editing.frontDesc}
                onChange={(e) => setEditing({ ...editing, frontDesc: e.target.value })}
              />
            </label>
            <label className="cards-page__field">
              <span>{t('cardsFieldBackDesc')}</span>
              <textarea
                rows={2}
                value={editing.backDesc}
                onChange={(e) => setEditing({ ...editing, backDesc: e.target.value })}
              />
            </label>

            {nbsKeys.length > 0 ? (
              <div className="cards-page__nbs" data-testid="cards-nbs-cache">
                <div className="cards-page__nbs-tabs" role="tablist">
                  {nbsKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      className={`cards-page__nbs-tab${nbsTab === key ? ' is-active' : ''}`}
                      aria-selected={nbsTab === key}
                      onClick={() => setNbsTab(key)}
                    >
                      {key}
                    </button>
                  ))}
                </div>
                <pre className="cards-page__nbs-body" data-testid="cards-nbs-body">
                  {nbsTab ? editing.nbsCache[nbsTab] ?? '' : ''}
                </pre>
              </div>
            ) : null}

            <div className="cards-page__modal-actions">
              <button
                type="button"
                className="cards-page__btn cards-page__btn--ghost"
                onClick={() => { setEditing(null); setEditBaseline(null); }}
              >
                {t('cardsClose')}
              </button>
              <button
                type="button"
                className="cards-page__btn cards-page__btn--primary"
                data-testid="cards-edit-save"
                disabled={!editDirty}
                onClick={() => void handleSaveEdit()}
              >
                {t('cardsSave')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
